import HubertCombobox from 'components/HubertUI/HubertCombobox';
import type { ComponentProps } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TSelectBase } from 'types/general.types';
import type { EQuestionType } from '@hubertai/screening-question-types';
import { CreateableQuestionTypes } from 'types/questions/question.types.v4';
import QuestionTypeDescription from 'components/Questionsv2/builder/QuestionTypeDescription';
import { useUser } from 'hooks/useUser';
import { isHubertAdminUser } from 'types/user.types';

const typeToKey = (type: string) => {
  if (!type) return '';
  return type.replace(/[-_]([a-z])/g, (_, c: string) => c.toUpperCase());
};

interface Props extends ComponentProps<'div'> {
  isMulti?: boolean;
  value?: string | string[];
  onValueChange?: (value: string | string[]) => void;
  disabled?: boolean;
  showDescription?: boolean;
}

const QuestionTypeCombobox = ({
  isMulti = false,
  onValueChange,
  value,
  disabled = false,
  showDescription = false,
  ...props
}: Props) => {
  const { t } = useTranslation('Questionsv2');
  const [items, setItems] = useState<TSelectBase[]>([]);
  const user = useUser();
  const isHubertAdmin = isHubertAdminUser(user);

  useEffect(() => {
    const x: TSelectBase[] = [];
    CreateableQuestionTypes.map((value: string) => {
      if (['video', 'audio', 'scheduling'].includes(value) && !isHubertAdmin) {
        return;
      }
      const key = typeToKey(value as string);
      const label = key ? t(`questionTypes.labels.${key}`) : value;
      x.push({ label, value });
    });
    setItems(x);
  }, [value, t, isHubertAdmin]);

  const selectedValues = useMemo<string[]>(() => {
    if (!value) return [];
    return Array.isArray(value) ? value : [value];
  }, [value]);

  return (
    <div {...props}>
      <HubertCombobox
        itemName={t('questionTypeCombobox.itemName')}
        isMulti={isMulti}
        items={items}
        onValueChange={onValueChange}
        value={value}
        disabled={disabled}
      />
      {!isMulti && showDescription && selectedValues.length > 0 && (
        <div className="mt-3 grid gap-2">
          {selectedValues.map((val) => {
            const type = val as EQuestionType;
            return <QuestionTypeDescription key={val} type={type} />;
          })}
        </div>
      )}
    </div>
  );
};

export default QuestionTypeCombobox;
